import { useState, useMemo } from "react";
import { CalendarRange, ChevronLeft, ChevronRight, Save, Copy, Zap, ClipboardPaste } from "lucide-react";
import { computeDuration, fmtHours, fmtMoney } from "@/lib/calc";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function mondayOf(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const day = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toStr(d) {
  return d.toISOString().slice(0, 10);
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function freshDay(settings, projects) {
  return {
    enabled: false,
    start_time: "",
    end_time: "",
    project: projects[0] || "General",
    hourly_rate: settings.hourly_rate,
    multiplier: settings.multiplier,
    notes: "",
  };
}

export default function BulkEntry({ settings, projects, onSubmitBulk }) {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date().toISOString().slice(0, 10)));
  const [days, setDays] = useState(() => Array.from({ length: 7 }, () => freshDay(settings, projects)));
  const [saving, setSaving] = useState(false);
  const [tmpl, setTmpl] = useState({
    start_time: "",
    end_time: "",
    project: projects[0] || "General",
    hourly_rate: settings.hourly_rate,
    multiplier: settings.multiplier,
    notes: "",
  });

  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const update = (i, key, value) => {
    setDays((prev) => prev.map((d, idx) => (idx === i ? { ...d, [key]: value } : d)));
  };

  const updateTmpl = (key, value) => setTmpl((t) => ({ ...t, [key]: value }));

  // Apply the quick-fill template to a set of day indices and enable them
  const applyTemplate = (indices) => {
    const set = new Set(indices);
    setDays((prev) =>
      prev.map((d, idx) =>
        set.has(idx)
          ? {
              ...d,
              enabled: true,
              start_time: tmpl.start_time,
              end_time: tmpl.end_time,
              project: tmpl.project,
              hourly_rate: tmpl.hourly_rate,
              multiplier: tmpl.multiplier,
              notes: tmpl.notes,
            }
          : d
      )
    );
  };

  const toggle = (i) => update(i, "enabled", !days[i].enabled);

  const copyToWeek = (i) => {
    const src = days[i];
    setDays((prev) => prev.map((d) => ({ ...d, start_time: src.start_time, end_time: src.end_time, project: src.project, hourly_rate: src.hourly_rate, multiplier: src.multiplier })));
  };

  const shiftWeek = (n) => {
    setWeekStart((w) => addDays(w, n * 7));
  };

  const totals = days.reduce(
    (acc, d) => {
      if (!d.enabled) return acc;
      const dur = computeDuration(d.start_time, d.end_time);
      acc.hours += dur;
      acc.earnings += dur * Number(d.hourly_rate || 0) * Number(d.multiplier || 0);
      acc.count += 1;
      return acc;
    },
    { hours: 0, earnings: 0, count: 0 }
  );

  const submit = async () => {
    setSaving(true);
    try {
      const payloads = days
        .map((d, i) => ({ d, i }))
        .filter(({ d }) => d.enabled)
        .map(({ d, i }) => ({
          date: toStr(weekDates[i]),
          start_time: d.start_time,
          end_time: d.end_time,
          project: d.project,
          notes: d.notes,
          hourly_rate: Number(d.hourly_rate),
          multiplier: Number(d.multiplier),
        }));
      const ok = await onSubmitBulk(payloads);
      if (ok) setDays(Array.from({ length: 7 }, () => freshDay(settings, projects)));
    } finally {
      setSaving(false);
    }
  };

  const cell = "bg-[#14161c] border border-[#2b2f3d] rounded-md px-2 py-1.5 text-[#f3f4f8] outline-none focus:border-amber-500 transition-colors font-mono-data text-xs w-full disabled:opacity-40";
  const head = "font-mono-data text-[10px] uppercase tracking-[0.16em] text-[#62697e] px-1";

  const weekLabel = `${weekDates[0].toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} – ${weekDates[6].toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" })}`;

  return (
    <div data-testid="bulk-entry" className="fade-up">
      <div className="bg-[#181a22] border border-[#2b2f3d] rounded-xl p-5">
        {/* Week picker */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <CalendarRange className="w-5 h-5 text-amber-500" />
            <h3 className="font-display text-xl font-bold uppercase tracking-tight text-[#f3f4f8]">Weekly Bulk Entry</h3>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="bulk-prev-week" onClick={() => shiftWeek(-1)} className="p-2 rounded-lg text-[#9fa6b9] hover:text-amber-500 hover:bg-[#20232e] border border-[#2b2f3d] transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span data-testid="bulk-week-label" className="font-mono-data text-xs text-[#f3f4f8] px-3 py-2 rounded-lg bg-[#14161c] border border-[#2b2f3d] whitespace-nowrap">{weekLabel}</span>
            <button data-testid="bulk-next-week" onClick={() => shiftWeek(1)} className="p-2 rounded-lg text-[#9fa6b9] hover:text-amber-500 hover:bg-[#20232e] border border-[#2b2f3d] transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <p className="text-xs text-[#62697e] mb-4 font-mono-data">Tick the days you worked overtime, set the times, then save the whole week at once.</p>

        {/* Quick fill */}
        <div data-testid="bulk-quick-fill" className="mb-5 bg-[#14161c] border border-amber-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-amber-500" />
            <span className="font-mono-data text-[11px] uppercase tracking-[0.18em] text-amber-500/90 font-semibold">Quick Fill</span>
            <span className="font-mono-data text-[11px] text-[#62697e]">— set once, apply to many days</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 items-end">
            <div>
              <label className={head}>Start</label>
              <input data-testid="quickfill-start" type="time" value={tmpl.start_time} onChange={(e) => updateTmpl("start_time", e.target.value)} className={`mt-1 ${cell}`} />
            </div>
            <div>
              <label className={head}>End</label>
              <input data-testid="quickfill-end" type="time" value={tmpl.end_time} onChange={(e) => updateTmpl("end_time", e.target.value)} className={`mt-1 ${cell}`} />
            </div>
            <div className="col-span-2 lg:col-span-1">
              <label className={head}>Project</label>
              <Select value={tmpl.project} onValueChange={(v) => updateTmpl("project", v)}>
                <SelectTrigger data-testid="quickfill-project" className="mt-1 bg-[#14161c] border-[#2b2f3d] text-[#f3f4f8] font-mono-data text-xs h-[34px] px-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#181a22] border-[#2b2f3d] text-[#f3f4f8]">
                  {projects.map((p) => (
                    <SelectItem key={p} value={p} className="font-mono-data text-xs">{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={head}>Rate</label>
              <input data-testid="quickfill-rate" type="number" step="0.01" min="0" value={tmpl.hourly_rate} onChange={(e) => updateTmpl("hourly_rate", e.target.value)} className={`mt-1 ${cell}`} />
            </div>
            <div>
              <label className={head}>Mult</label>
              <input data-testid="quickfill-multiplier" type="number" step="0.1" min="0" value={tmpl.multiplier} onChange={(e) => updateTmpl("multiplier", e.target.value)} className={`mt-1 ${cell}`} />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <button data-testid="quickfill-apply-all" onClick={() => applyTemplate([0, 1, 2, 3, 4, 5, 6])} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono-data text-xs uppercase tracking-wide bg-amber-500 text-[#0c0d10] font-bold hover:bg-amber-400 transition-colors active:scale-[0.98]">
              <ClipboardPaste className="w-3.5 h-3.5" /> Apply to all 7 days
            </button>
            <button data-testid="quickfill-apply-weekdays" onClick={() => applyTemplate([0, 1, 2, 3, 4])} className="px-3 py-1.5 rounded-lg font-mono-data text-xs uppercase tracking-wide bg-[#20232e] text-[#9fa6b9] border border-[#2b2f3d] hover:border-amber-500 hover:text-amber-500 transition-colors">
              Weekdays (Mon–Fri)
            </button>
            <button data-testid="quickfill-apply-weekend" onClick={() => applyTemplate([5, 6])} className="px-3 py-1.5 rounded-lg font-mono-data text-xs uppercase tracking-wide bg-[#20232e] text-[#9fa6b9] border border-[#2b2f3d] hover:border-amber-500 hover:text-amber-500 transition-colors">
              Weekend (Sat–Sun)
            </button>
          </div>
        </div>

        {/* Header (desktop) */}
        <div className="hidden lg:grid grid-cols-[1.1fr_0.8fr_0.8fr_1.2fr_0.8fr_0.7fr_0.8fr_0.9fr_auto] gap-2 mb-2">
          <span className={head}>Day</span>
          <span className={head}>Start</span>
          <span className={head}>End</span>
          <span className={head}>Project</span>
          <span className={head}>Rate</span>
          <span className={head}>Mult</span>
          <span className={`${head} text-right`}>Hours</span>
          <span className={`${head} text-right`}>Payout</span>
          <span className={head}></span>
        </div>

        <div className="space-y-2">
          {days.map((d, i) => {
            const dur = computeDuration(d.start_time, d.end_time);
            const pay = dur * Number(d.hourly_rate || 0) * Number(d.multiplier || 0);
            const isWeekend = i >= 5;
            return (
              <div
                key={i}
                data-testid="bulk-day-row"
                className={`grid grid-cols-2 lg:grid-cols-[1.1fr_0.8fr_0.8fr_1.2fr_0.8fr_0.7fr_0.8fr_0.9fr_auto] gap-2 items-center rounded-lg p-2 lg:p-1.5 border transition-colors ${d.enabled ? "border-amber-500/40 bg-amber-500/[0.03]" : "border-[#2b2f3d] lg:border-transparent bg-[#14161c]/30 lg:bg-transparent"}`}
              >
                <label className="col-span-2 lg:col-span-1 flex items-center gap-2 cursor-pointer select-none">
                  <input
                    data-testid="bulk-day-toggle"
                    type="checkbox"
                    checked={d.enabled}
                    onChange={() => toggle(i)}
                    className="w-4 h-4 accent-amber-500 cursor-pointer"
                  />
                  <span className="font-mono-data text-xs">
                    <span className={isWeekend ? "text-[#f97316] font-bold" : "text-[#f3f4f8] font-bold"}>{DAY_LABELS[i]}</span>
                    <span className="text-[#62697e] ml-1.5">{weekDates[i].toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" })}</span>
                  </span>
                </label>
                <input data-testid="bulk-start" type="time" disabled={!d.enabled} value={d.start_time} onChange={(e) => update(i, "start_time", e.target.value)} className={cell} />
                <input data-testid="bulk-end" type="time" disabled={!d.enabled} value={d.end_time} onChange={(e) => update(i, "end_time", e.target.value)} className={cell} />
                <div className="col-span-2 lg:col-span-1">
                  <Select value={d.project} onValueChange={(v) => update(i, "project", v)} disabled={!d.enabled}>
                    <SelectTrigger data-testid="bulk-project" className="bg-[#14161c] border-[#2b2f3d] text-[#f3f4f8] font-mono-data text-xs h-[34px] px-2 disabled:opacity-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#181a22] border-[#2b2f3d] text-[#f3f4f8]">
                      {projects.map((p) => (
                        <SelectItem key={p} value={p} className="font-mono-data text-xs">{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <input data-testid="bulk-rate" type="number" step="0.01" min="0" disabled={!d.enabled} value={d.hourly_rate} onChange={(e) => update(i, "hourly_rate", e.target.value)} className={cell} />
                <input data-testid="bulk-multiplier" type="number" step="0.1" min="0" disabled={!d.enabled} value={d.multiplier} onChange={(e) => update(i, "multiplier", e.target.value)} className={cell} />
                <span className={`font-mono-data text-xs font-bold lg:text-right ${d.enabled ? "text-amber-500" : "text-[#62697e]"}`}>{dur.toFixed(2)}h</span>
                <span className={`font-mono-data text-xs font-bold lg:text-right ${d.enabled ? "text-emerald-500" : "text-[#62697e]"}`}>{fmtMoney(pay, settings.currency)}</span>
                <div className="flex items-center justify-end">
                  <button data-testid="bulk-copy-to-week" onClick={() => copyToWeek(i)} disabled={!d.enabled} className="p-1.5 rounded-md text-[#9fa6b9] hover:text-amber-500 hover:bg-[#20232e] transition-colors disabled:opacity-30" title="Copy times to all days">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Totals + submit */}
        <div className="mt-5 pt-4 border-t border-[#2b2f3d] flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-5">
            <span className="font-mono-data text-[11px] uppercase tracking-[0.18em] text-[#62697e]">Week total</span>
            <span data-testid="bulk-total-hours" className="font-mono-data font-bold text-amber-500">{fmtHours(totals.hours)}</span>
            <span data-testid="bulk-total-earnings" className="font-mono-data font-bold text-emerald-500">{fmtMoney(totals.earnings, settings.currency)}</span>
          </div>
          <button
            data-testid="bulk-submit-button"
            onClick={submit}
            disabled={saving || totals.count === 0}
            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-[#0c0d10] font-display font-bold uppercase tracking-wide rounded-lg px-5 py-2.5 transition-colors active:scale-[0.99]"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : `Save ${totals.count} ${totals.count === 1 ? "Day" : "Days"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
